import {
    Box,
    Chip,
    LinearProgress,
    Paper,
    Typography,
    keyframes,
} from '@mui/material';
import { InFlightMessage, InFlightStatus } from '../../interface/returnOps';

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.6; }
`;

const statusConfig: Record<InFlightStatus, { label: string; color: 'info' | 'warning' | 'success' | 'error' }> = {
    started: { label: 'Started', color: 'info' },
    processing: { label: 'Processing', color: 'warning' },
    completed: { label: 'Completed', color: 'success' },
    failed: { label: 'Failed', color: 'error' },
};

const getRelativeTime = (dateStr: string): string => {
    const seconds = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
    if (seconds < 60) return `${seconds}s ago`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    return `${Math.floor(minutes / 60)}h ago`;
};

interface InFlightMessageCardProps {
    message: InFlightMessage;
}

const InFlightMessageCard = ({ message }: InFlightMessageCardProps) => {
    const { label, color } = statusConfig[message.status];
    const { business, progress: prog } = message;
    const progressPct = prog?.totalChunks
        ? Math.round(((prog.currentChunk ?? 0) / prog.totalChunks) * 100)
        : undefined;

    return (
        <Paper
            variant="outlined"
            sx={{
                p: 2,
                opacity: (message.status === 'completed' || message.status === 'failed') ? 0.6 : 1,
                transition: 'opacity 0.5s ease',
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Chip
                    label={label}
                    color={color}
                    size="small"
                    sx={{
                        fontWeight: 600,
                        animation: message.status === 'processing' ? `${pulse} 1.5s ease-in-out infinite` : 'none',
                    }}
                />
                <Typography variant="caption" color="text.secondary">
                    {getRelativeTime(message.startedAt)}
                </Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: progressPct !== undefined ? 1 : 0 }}>
                {business?.institutionName && (
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {business.institutionName}
                    </Typography>
                )}
                {business?.loanNumber && (
                    <Typography variant="body2" color="text.secondary">
                        Loan: {business.loanNumber}
                    </Typography>
                )}
                {business?.fileName && (
                    <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 200 }}>
                        {business.fileName}
                    </Typography>
                )}
            </Box>

            {prog?.description && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: progressPct !== undefined ? 0.5 : 0 }}>
                    {prog.description}
                </Typography>
            )}

            {progressPct !== undefined && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LinearProgress
                        variant="determinate"
                        value={progressPct}
                        sx={{ flex: 1 }}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ minWidth: 35 }}>
                        {progressPct}%
                    </Typography>
                </Box>
            )}

            {message.error && (
                <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                    {message.error}
                </Typography>
            )}
        </Paper>
    );
};

export default InFlightMessageCard;
